import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SavedTestcaseFilter } from '../../../../models/saved-testcase-filter.model';

@Component({
  selector: 'app-saved-testcase-filter-chips',
  imports: [TranslatePipe],
  templateUrl: './saved-testcase-filter-chips.component.html',
  styleUrl: './saved-testcase-filter-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedTestcaseFilterChipsComponent {
  readonly filters = input<SavedTestcaseFilter[]>([]);
  readonly activeName = input<string | null>(null);
  readonly canSave = input<boolean>(false);

  readonly filterSaved = output<string>();
  readonly filterSelected = output<SavedTestcaseFilter>();
  readonly filterDeleted = output<string>();

  readonly open = signal(false);
  readonly nameInput = signal('');

  readonly canPersist = computed(() => this.nameInput().trim().length > 0);

  private readonly root = viewChild<ElementRef<HTMLElement>>('root');
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');
  private readonly nameField = viewChild<ElementRef<HTMLInputElement>>('nameField');

  constructor() {
    effect(() => {
      if (this.open()) {
        this.nameField()?.nativeElement.focus();
      }
    });
  }

  toggle(): void {
    if (!this.canSave() && !this.open()) return;
    this.open.update((v) => !v);
  }

  close(returnFocus = false): void {
    this.open.set(false);
    this.nameInput.set('');
    if (returnFocus) {
      this.trigger()?.nativeElement.focus();
    }
  }

  onNameInput(event: Event): void {
    this.nameInput.set((event.target as HTMLInputElement).value);
  }

  onSaveKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.canPersist()) {
      event.preventDefault();
      this.save();
    }
  }

  save(): void {
    const name = this.nameInput().trim();
    if (!name) return;
    this.filterSaved.emit(name);
    this.close(true);
  }

  onSelect(filter: SavedTestcaseFilter): void {
    this.filterSelected.emit(filter);
  }

  onDelete(event: Event, name: string): void {
    event.stopPropagation();
    this.filterDeleted.emit(name);
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    const root = this.root()?.nativeElement;
    if (root && !root.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.open()) {
      this.close(true);
    }
  }
}
